import axios from 'axios';

import Button from 'components/button';
import Card from 'components/card';
import Checkbox from 'components/checkbox';
import InputField from 'components/fields/InputField';
import Header from 'components/text/header';
import UseApi from 'hooks/useApi';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

const modules = [
  'Package',
  'Coupon',
  'Booking',
  'WithdrawalRequest',
  'User',
  'ContactForm',
];
const roles = ['Create', 'Read', 'Update', 'Archive', 'Approve'];
const initialPermissions = modules.map((module) => ({ module, roles: [] }));

interface IProps {
  formValues?: {
    username: string;
    password: string;
    permissions: [{ module: 'string'; roles: ['string'] }];
  };
  type: 'Add' | 'Update';
  onSubmit?: () => void;
}
const AdminForm: React.FC<IProps> = ({ formValues, type, onSubmit }) => {
  const { id } = useParams();
  const [username, setUsername] = useState(formValues?.username || '');
  const [password, setPassword] = useState(formValues?.password || '');
  const [permissions, setPermissions] = useState(
    formValues?.permissions || initialPermissions,
  );

  const { patchApi, postApi } = UseApi();

  const resetForm = () => {
    setPermissions(initialPermissions);
    setUsername('');
    setPassword('');
  };

  const handleRoleChange = (index: any, event: any) => {
    const newPermissions = [...permissions];
    if (event.target.checked) {
      newPermissions[index].roles.push(event.target.value);
    } else {
      newPermissions[index].roles = newPermissions[index].roles.filter(
        (role) => role !== event.target.value,
      );
    }
    setPermissions(newPermissions);
  };

  const handleSelectAll = (index: number) => {
    const newPermissions = [...permissions];
    newPermissions[index].roles = roles;
    setPermissions(newPermissions);
  };

  const handleSubmit = async () => {
    let res;
    if (type === 'Update') {
      res = await patchApi(`admins/${id}`, {
        username,
        permissions,
      });
    } else {
      res = await postApi('admins', {
        username,
        password,
        permissions,
      });
    }
    if (res?.data.message) {
      resetForm();
      onSubmit?.();
    }
  };

  return (
    <Card extra="p-4 my-4 flex flex-col gap-4">
      <Header extraClasses="mb-6">{type} Admin</Header>
      <InputField
        variant="auth"
        extra="mb-3"
        label="Username*"
        placeholder="Username"
        id="username"
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        name="username"
      />
      {type === 'Add' && (
        <InputField
          variant="auth"
          extra="mb-3"
          label="Password*"
          placeholder="Password"
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          name="password"
        />
      )}
      <Header>Permissions</Header>
      {permissions.map((permission, index) => (
        <div key={index}>
          <Header extraClasses="text-gray-500 text-lg">
            {permission.module}
          </Header>
          <div className="flex gap-5">
            {roles.map((role) => (
              <Checkbox
                label={role}
                value={role}
                checked={permission.roles.includes(role)}
                onChange={(e: any) => handleRoleChange(index, e)}
                extra="my-2"
              />
            ))}
            <button
              className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10"
              onClick={() => handleSelectAll(index)}
            >
              Select All
            </button>
          </div>
        </div>
      ))}
      <Button onClick={handleSubmit} extraClasses="mx-auto">
        {type} Admin
      </Button>
    </Card>
  );
};

export default AdminForm;
